import { Message, Participant } from "@shared/factories";
import { RoomModel, WsChatMessageModel, WsChatResponse, WsChatUserMessageModel, WsResponse } from "@shared/models";

import { FormatDatesService } from "@shared/services";

export class Room {
  id:                  number;
  myId:                number;
  createdById:         number;
  openedAt:            Date;
  roomName:            string;

  participants:        Participant[];

  messages:            Message[];
  messagesCount:       number;
  unreadMessagesCount: number;

  active:              boolean;
  recent:              boolean;
  constructor(data: RoomModel, userId?: number) {
    this.id                  = data.id                   ? data.id                                        : null;
    this.myId                = data.myId || userId       ? data.myId || userId                            : null;
    this.createdById         = data.created_by_id        ? data.created_by_id                             : null;

    this.roomName            = data.room_name            ? data.room_name                                 : null;
    this.openedAt            = FormatDatesService.parseDate(data.openedAt);

    this.participants        = data.participants?.length ? data.participants.map(p => new Participant(p)) : [];

    this.messages            = data.messages?.length     ? data.messages.map(m => new Message(m, userId)) : [];
    this.messagesCount       = data.messages_count       ? data.messages_count                            : null;
    this.unreadMessagesCount = Math.max(0,this.getUnreadMessagesCount());

    this.active              = data.active               ? data.active                                    : null;
    this.recent              = data.recent               ? data.recent                                    : null;
  }

  get owner(): Participant {
    return this.participants?.find(p => p.isOwner);
  }

  get activeMessages(): Message[] {
    return this.messages?.filter(p => !p.deletedAt);
  }

  get activeParticipants(): Participant[] {
    return this.participants?.filter(p => !p.deletedAt);
  }

  get meAsPartisipant(): Participant {
    return this.participants?.find(p => p.userId === this.myId);
  }

  get isParticipant(): boolean {
    return this.meAsPartisipant && !this.meAsPartisipant.deletedAt;
  }

  get latestMessage(): Message {
    return this.activeMessages?.length && this.activeMessages?.sort((a,b) => b.seq - a.seq)[0];
  }

  get latestOwnerMessage(): Message {
    return this.activeMessages?.length && this.activeMessages.filter(m => m.participantId === this.owner.id).sort((a,b) => b.seq - a.seq)[0];
  }

  private getUnreadMessagesCount(): number {
    return this.meAsPartisipant ? this.meAsPartisipant.unreadMessagesCount : this.messagesCount;
  }

  adjustUnread(count: number): void {
    if (count > 0) this.messagesCount += count;
    if (this.meAsPartisipant) {
      this.meAsPartisipant.unreadMessagesCount += count;
      this.unreadMessagesCount = Math.max(0,this.getUnreadMessagesCount());
    }
  }

  toJSON(): RoomModel {
    return {
      id:             this.id                   ? this.id                                : null,
      myId:           this.myId                 ? this.myId                              : null,
      created_by_id:  this.createdById          ? this.createdById                       : null,

      room_name:      this.roomName             ? this.roomName                          : null,
      openedAt:       FormatDatesService.parseDateISO(this.openedAt),

      messages_count: this.messagesCount        ? this.messagesCount                     : null,
      messages:       this.messages?.length     ? this.messages.map(m => m.toJSON())     : null,

      participants:   this.participants?.length ? this.participants.map(p => p.toJSON()) : null,

      recent:         this.recent               ? this.recent                            : null,
      active:         this.active               ? this.active                            : null
    };
  }

}

type ChatEventType =
  'bulk_message_create_result' |
  'bulk_message_update_result' |
  'join_room'                  |
  'leave_room'                 |
  'message_deleted'            |
  'message_updated'            |
  'new_message'                |
  'new_room'                   |
  'participant_offline'        |
  'participant_online'         |
  'seen_messages'              |
  'typing_message';

export class ChatEvent {
  type:                   ChatEventType;
  channel:                string;

  roomId:                 number;
  roomName:               string;

  participantId:          number;
  participantName:        string;
  participantConnectedAt: Date;
  isOwner:                boolean;

  id:                     number;
  bulkMessageId:          number;
  seq:                    number;
  text:                   string;

  messageIds:             number[];
  constructor(data: WsResponse) {
    let identifier              = data?.identifier                          ? JSON.parse(data.identifier)                  : null;
    let wsData                  = data?.message                             ? data.message as WsChatResponse               : null;
    let wsMessage               = wsData?.message                           ? wsData.message                               : null;

    this.type                   = wsData?.type                              ? wsData.type as ChatEventType                 : null;
    this.channel                = identifier?.channel                       ? identifier?.channel                          : null;

    this.roomId                 = identifier?.room_id || wsMessage?.room_id ? +(identifier?.room_id || wsMessage?.room_id) : null;
    this.roomName               = wsMessage?.room_name                      ? wsMessage.room_name                          : null;

    this.participantId          = wsMessage?.participant_id                 ? wsMessage.participant_id                     : null;
    this.participantName        = wsMessage?.full_name                      ? wsMessage.full_name                          : null;
    this.participantConnectedAt = FormatDatesService.parseDate(wsMessage?.connected_at);
    this.isOwner                = wsMessage?.is_owner                       ? wsMessage.is_owner                           : null;

    this.id                     = wsMessage?.id                             ? wsMessage.id                                 : null;
    this.bulkMessageId          = wsMessage?.bulk_message_id                ? wsMessage.bulk_message_id                    : null;
    this.seq                    = wsMessage?.seq                            ? wsMessage.seq                                : null;
    this.text                   = wsMessage?.text                           ? wsMessage.text                               : null;

    this.messageIds             = wsMessage?.message_ids                    ? wsMessage.message_ids                        : null;
  }

}
